import { getLocalApiOrigin } from '../localApi';
import { setAmbienceSuppressed } from './ambienceManager';
import { getEffectiveSfxVolume } from './soundSettings';
// The underwater music setters are imported lazily: musicManager sits in a
// circular chain with ambienceManager/subsurfaceAmbience, so a static import
// here can catch musicManager mid-evaluation (depending on module load order,
// e.g. a test that loads this chain first) and fail to link `setMusicUnderwater`.
// These are only ever called at runtime (subsurface enter/exit), long after all
// modules have finished loading, so a cached lazy import is behaviour-identical.
let _musicMod = null;
let _customMusicMod = null;
import('./musicManager').then((m) => { _musicMod = m; });
import('./customMusicManager').then((m) => { _customMusicMod = m; });
const setMusicUnderwater = (...a) => _musicMod?.setMusicUnderwater?.(...a);
const setCustomMusicUnderwater = (...a) => _customMusicMod?.setCustomMusicUnderwater?.(...a);

/**
 * Subsurface region-view ambience layer. While the player is viewing
 * the underground / underwater side of the playmat, the user's chosen
 * AMBIENCE (forest / swamp / etc) fades out and this module crossfades
 * in a cave + underwater loop. The user's MUSIC (built-in score or
 * a custom track) keeps playing. But routed through a Web Audio
 * lowpass + convolver-reverb chain so it sounds far away, like it's
 * echoing from outside a huge underground hall. Exiting the
 * subsurface view reverses both effects.
 *
 * Volume model for the cave/underwater pair: cave is dominant,
 * underwater plays at 70% of cave's level so the cave reads as the
 * primary signal. Both ride the SFX channel of the sound settings
 * (master x sfx, off when SFX is disabled), not the ambience slider.
 *
 * One HTMLAudioElement per layer, looped, fade-managed by a linear
 * gain ramp identical to the one in ambienceManager. Nothing here
 * persists. The active state lives entirely in module-local memory
 * and is tied to the game-board lifetime.
 */

const FADE_MS = 1500;
// Slower fade for mid-session mix shifts (sites added/removed,
// triggering a re-balance). 800ms keeps it smooth without dragging.
const MIX_FADE_MS = 800;
// Endpoints for the dynamic mix: the dominant layer plays at ~1.0,
// the non-dominant at ~0.2 (small background presence so the
// subsurface never goes single-channel even at extreme ratios).
const MIX_DOMINANT = 1.0;
const MIX_FLOOR = 0.2;
// Playback level for the cave + underwater pair, applied AFTER the
// mix factor and BEFORE the SFX channel of the sound settings.
// Decoupled from the user's ambience-volume slider: these clips are
// subsurface sound-effects, not user-pickable ambience tracks (the
// picker excludes them), so the slider should only control the
// player's chosen ambience. Not the subsurface cue. 0.7 lands at
// ~0.4 with the default mixer (master 0.7 x sfx 0.8), which read as
// comfortably audible without dominating.
const SUBSURFACE_AMBIENCE_LEVEL = 0.7;

const CAVE_FILE = 'snd-ambience-cave.mp3';
const UNDERWATER_FILE = 'snd-ambience-underwater.mp3';

let active = false;
let cave = null;
let underwater = null;
let caveFadeTimer = null;
let underwaterFadeTimer = null;

// Dynamic mix factors driven by the live ratio of water vs underground
// cells in the subsurface. setSubsurfaceMix updates these from
// GameBoard each frame. Defaults give the prior static behavior
// (cave dominant, underwater at 0.7) so the first frame of any
// transition reads correctly even before counts have been pushed.
let caveMixFactor = MIX_DOMINANT;
let underwaterMixFactor = 0.7;
let lastMixWater = -1;
let lastMixUnderground = -1;

function trackUrl(file) {
  return `${getLocalApiOrigin()}/game-assets/${file}`;
}

// Multiplied by the per-layer mix factor so cave/underwater shift in
// emphasis as the body distribution changes, then by the SFX channel
// so the cue obeys the sound settings. Independent of the user's
// ambience-volume slider, because these clips are subsurface
// sound-effects rather than user-pickable atmosphere tracks.
function caveTarget()       { return SUBSURFACE_AMBIENCE_LEVEL * caveMixFactor * getEffectiveSfxVolume(); }
function underwaterTarget() { return SUBSURFACE_AMBIENCE_LEVEL * underwaterMixFactor * getEffectiveSfxVolume(); }

function clearFade(which) {
  if (which === 'cave' && caveFadeTimer) {
    clearInterval(caveFadeTimer);
    caveFadeTimer = null;
  } else if (which === 'underwater' && underwaterFadeTimer) {
    clearInterval(underwaterFadeTimer);
    underwaterFadeTimer = null;
  }
}

function fadeTo(which, target, durationMs, onDone) {
  const el = which === 'cave' ? cave : underwater;
  if (!el) { if (onDone) onDone(); return; }
  clearFade(which);
  const start = el.volume;
  const steps = 30;
  const stepMs = Math.max(8, Math.floor(durationMs / steps));
  let i = 0;
  const id = setInterval(() => {
    i++;
    const liveEl = which === 'cave' ? cave : underwater;
    if (!liveEl) {
      clearInterval(id);
      if (which === 'cave') caveFadeTimer = null;
      else underwaterFadeTimer = null;
      if (onDone) onDone();
      return;
    }
    if (i >= steps) {
      liveEl.volume = target;
      clearInterval(id);
      if (which === 'cave') caveFadeTimer = null;
      else underwaterFadeTimer = null;
      if (onDone) onDone();
      return;
    }
    liveEl.volume = start + (target - start) * (i / steps);
  }, stepMs);
  if (which === 'cave') caveFadeTimer = id;
  else underwaterFadeTimer = id;
}

function ensureLayer(which) {
  const file = which === 'cave' ? CAVE_FILE : UNDERWATER_FILE;
  const existing = which === 'cave' ? cave : underwater;
  if (existing) return existing;
  const el = new Audio(trackUrl(file));
  el.loop = true;
  el.volume = 0;
  if (which === 'cave') cave = el;
  else underwater = el;
  return el;
}

function startLayer(which, target) {
  const el = ensureLayer(which);
  el.volume = 0;
  const playing = el.play();
  if (playing && typeof playing.catch === 'function') {
    playing.catch(() => {
      // Autoplay should already be unlocked. The user clicked the
      // subsurface toggle to get here. If it isn't, retry on the next
      // gesture so the cue isn't lost entirely.
      const resume = () => {
        if (!active) return;
        el.play().catch(() => {});
        document.removeEventListener('pointerdown', resume);
        document.removeEventListener('keydown', resume);
      };
      document.addEventListener('pointerdown', resume, { once: true });
      document.addEventListener('keydown', resume, { once: true });
    });
  }
  fadeTo(which, target, FADE_MS);
}

function teardownLayer(which) {
  const el = which === 'cave' ? cave : underwater;
  if (!el) return;
  fadeTo(which, 0, FADE_MS, () => {
    try { el.pause(); } catch {}
    try { el.src = ''; } catch {}
    if (which === 'cave') cave = null;
    else underwater = null;
  });
}

/**
 * Update the cave/underwater volume balance from the live count of
 * subsurface cells. More water cells → underwater layer rises and
 * cave layer recedes; more underground cells → the reverse. Both
 * layers remain audible at any ratio (floor of MIX_FLOOR each), so
 * the subsurface always reads as a layered space. Never single-
 * channel even at extreme distributions.
 *
 * Safe to call every frame; dedupes when counts are unchanged and
 * skips fade churn while values are identical to the previous push.
 * Also safe to call before `enterSubsurfaceAmbience()`. The factors
 * update immediately so the initial layer fade-in starts at the
 * correct ratio without a secondary re-fade after entry.
 */
export function setSubsurfaceMix(waterCells, undergroundCells) {
  const w = Math.max(0, Math.floor(waterCells) || 0);
  const u = Math.max(0, Math.floor(undergroundCells) || 0);
  if (w === lastMixWater && u === lastMixUnderground) return;
  lastMixWater = w;
  lastMixUnderground = u;

  const total = w + u;
  if (total === 0) {
    // No subsurface bodies. Fall back to the prior static defaults
    // so the audio still has a balanced cave-leaning layered feel.
    caveMixFactor = MIX_DOMINANT;
    underwaterMixFactor = 0.7;
  } else {
    const ratio = w / total;
    // Linear interpolation between MIX_DOMINANT and MIX_FLOOR on
    // opposite endpoints. At ratio=0 (all underground), cave is
    // dominant; at ratio=1 (all water), underwater is dominant; the
    // other layer rides at MIX_FLOOR.
    caveMixFactor       = MIX_FLOOR + (MIX_DOMINANT - MIX_FLOOR) * (1 - ratio);
    underwaterMixFactor = MIX_FLOOR + (MIX_DOMINANT - MIX_FLOOR) * ratio;
  }

  // Fade currently-playing layers to the new targets. fadeTo cancels
  // any in-flight ramp so rapid mix changes don't stack timers.
  if (cave) fadeTo('cave', caveTarget(), MIX_FADE_MS);
  if (underwater) fadeTo('underwater', underwaterTarget(), MIX_FADE_MS);
}

/**
 * Re-apply the gain after a sound-settings change. The layers own their
 * own audio elements, so like ambience they need their own poke.
 */
export function updateSubsurfaceAmbienceVolume() {
  if (!active) return;
  if (cave) fadeTo('cave', caveTarget(), MIX_FADE_MS);
  if (underwater) fadeTo('underwater', underwaterTarget(), MIX_FADE_MS);
}

/** Push the subsurface ambience layer on top of the user's tracks. */
export function enterSubsurfaceAmbience() {
  if (active) return;
  active = true;

  setAmbienceSuppressed(true, { fadeMs: FADE_MS });
  // Music keeps playing on subsurface. Both the built-in score and
  // any custom track route through a lowpass + reverb chain that
  // ramps in over FADE_MS, making the track sound far away (huge
  // underground hall). Surface-return un-ramps them back to dry.
  setMusicUnderwater(1, FADE_MS);
  setCustomMusicUnderwater(1, FADE_MS);

  startLayer('cave', caveTarget());
  startLayer('underwater', underwaterTarget());
  // No ambience-slider subscription: cave + underwater play at
  // SUBSURFACE_AMBIENCE_LEVEL × mix factor × SFX channel, independent
  // of the user's ambience-volume slider. setSubsurfaceMix and
  // updateSubsurfaceAmbienceVolume drive post-entry volume changes.
}

/** Pop the subsurface layer. Fade out cue, fade in the user's tracks. */
export function exitSubsurfaceAmbience() {
  if (!active) return;
  active = false;

  teardownLayer('cave');
  teardownLayer('underwater');

  setAmbienceSuppressed(false, { fadeMs: FADE_MS });
  setMusicUnderwater(0, FADE_MS);
  setCustomMusicUnderwater(0, FADE_MS);
}

/**
 * Hard teardown. Used when the game board unmounts mid-subsurface.
 * Skips the fade since the host component is being destroyed anyway,
 * and clears suppression so the next session starts from a clean
 * slate.
 */
export function stopSubsurfaceAmbience() {
  if (!active && !cave && !underwater) return;
  active = false;
  clearFade('cave');
  clearFade('underwater');
  if (cave) {
    try { cave.pause(); } catch {}
    try { cave.src = ''; } catch {}
    cave = null;
  }
  if (underwater) {
    try { underwater.pause(); } catch {}
    try { underwater.src = ''; } catch {}
    underwater = null;
  }
  setAmbienceSuppressed(false, { fadeMs: 0 });
  setMusicUnderwater(0, 0);
  setCustomMusicUnderwater(0, 0);
  // Reset the dedupe cache so a future subsurface session re-pushes
  // the mix on its first frame regardless of count equality.
  lastMixWater = -1;
  lastMixUnderground = -1;
}
